import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription, firstValueFrom, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap, finalize } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { RulesService, CreateRulePayload, Rule } from './rules.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { MessageHandlerService } from '../services/message-handler.service';
import {
  ERROR_MESSAGES,
  RULES_JSON_UPLOAD_LIMIT,
  LAYOUT_JSON_FILE_EXTENSION,
  RULES_JSON_INVALID_FILE_TYPE,
  RULES_JSON_INVALID_FORMAT,
  RULES_JSON_EMPTY,
  RULES_JSON_NO_RULES
} from '../common/Constants';

@Component({
  selector: 'app-rules',
  templateUrl: './rules.component.html',
  styleUrls: ['./rules.component.scss'],
})
export class RulesComponent implements OnInit, OnDestroy {
  dialogVisible = false;
  isSubmitting = false;
  isExportingRules = false;
  isEditMode = false;
  selectedRecord: any = null;
  rulesForm: FormGroup | null = null;

  selectedJsonFile: File | null = null;
  uploadProgressBar = false;
  uploadProgressValue = 0;
  isFileUploaded = false;
  uploadedFileName: string | null = null;

  ruleRecords: any[] = [];
  searchInput = '';
  pageNo = 0;
  noOfRecordsPerRow = 10;
  rowsPerPageOptions: number[] = [10, 20, 30];
  totalRecords = 0;

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private refreshSubject = new Subject<void>();

  collapsedSections: { [key: string]: boolean } = {
    rules: false,
  };

  collapsedRules: { [key: number]: boolean } = {};
  collapsedSuggestions: { [key: number]: boolean } = {};

  matchTypeOptions = ['EXACT', 'STARTS_WITH'];
  alertLabelOptions = ['RECOMMENDATION', 'SUGGESTION'];
  suggestionOptions = ['ERROR', 'WARNING', 'CORRECT'];

  constructor(
    private readonly rulesService: RulesService,
    private readonly progressBarService: ProgressBarService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly confirmationService: ConfirmationService,
    private readonly formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initSearchAndListPipeline();
    this.getRules();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private initSearchAndListPipeline(): void {
    const search$ = this.searchSubject.pipe(debounceTime(1000), distinctUntilChanged());
    const refresh$ = this.refreshSubject.asObservable();

    this.subscriptions.push(
      merge(search$, refresh$)
        .pipe(
          tap(() => {
            this.progressBarService.setProgressBarVisibility(true);
          }),
          switchMap((searchTerm) => {
            const term = typeof searchTerm === 'string' ? searchTerm.trim() : this.searchInput.trim();

            return this.rulesService
              .getAll({
                page: this.pageNo + 1,
                limit: this.noOfRecordsPerRow,
                sortBy: 'createdAt',
                sortOrder: 'desc',
                search: term || undefined,
              })
              .pipe(
                catchError((error) => {
                  return of({
                    success: false,
                    errorMessage: error?.errorMessage || error?.message || error,
                  });
                }),
              );
          }),
        )
        .subscribe((res: any) => {
          if (this.isSuccessResponse(res)) {
            const rawData = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.content?.data)
                ? res.content.data
                : [];

            this.ruleRecords = rawData || [];
            this.totalRecords =
              res.totalItems ??
              res.content?.pagination?.total ??
              res.content?.totalElements ??
              res.total ??
              rawData.length;

            if (this.ruleRecords.length === 0 && this.totalRecords > 0 && this.pageNo > 0) {
              this.pageNo = Math.max(0, this.pageNo - 1);
              this.refreshSubject.next();
              return;
            }
          } else {
            this.ruleRecords = [];
            this.totalRecords = 0;
            if (res?.errorMessage) {
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                res.errorMessage,
              );
            }
          }
          this.progressBarService.setProgressBarVisibility(false);
        }),
    );
  }

  private isSuccessResponse(res: any): boolean {
    if (res?.success === true) return true;
    if (res?.statusCode >= 200 && res?.statusCode < 300) return true;
    if (res?.content && !res?.errorMessage) return true;
    if (Array.isArray(res)) return true;
    if (Array.isArray(res?.data)) return true;
    return false;
  }

  getRules(): void {
    this.refreshSubject.next();
  }

  onSearchKeyup(): void {
    this.pageNo = 0;
    this.searchSubject.next(this.searchInput.trim());
  }

  paginate(event: any): void {
    this.pageNo = event?.page ?? 0;
    this.noOfRecordsPerRow = event?.rows ?? this.noOfRecordsPerRow;
    this.refreshSubject.next();
  }

  private initForm(): void {
    try {
      this.rulesForm = this.formBuilder.group({
        rules: this.formBuilder.array([this.createRuleFormGroup()]),
      });

      this.collapsedSections = { rules: false };
      this.collapsedRules = { 0: false };
      this.collapsedSuggestions = { 0: true };
    } catch (error) {
      console.error('Error initializing form:', error);
      this.rulesForm = null;
    }
  }

  private createRuleFormGroup(): FormGroup {
    return this.formBuilder.group({
      id: ['', [Validators.required]],
      match: this.formBuilder.group({
        type: ['EXACT', [Validators.required]],
        value: ['', [Validators.required]],
      }),
      alertLabel: [''],
      statusType: [''],
      considerMessage: [''],
      remindersMessage: [''],
      suggestions: this.formBuilder.array([]),
    });
  }

  private createSuggestionFormGroup(): FormGroup {
    return this.formBuilder.group({
      option: [null, [Validators.required]],
      suggestion: ['ERROR', [Validators.required]],
      isMcqCheckedRequired: [false],
      requiredPreReqAnswer: [''],
      isAgainstOrthoGroup: [false],
    });
  }

  get rulesFormArray(): FormArray {
    if (!this.rulesForm) {
      return this.formBuilder.array([]) as FormArray;
    }
    return this.rulesForm.get('rules') as FormArray;
  }

  getSuggestionsFormArray(ruleIndex: number): FormArray {
    if (!this.rulesForm || !this.rulesFormArray.length) {
      return this.formBuilder.array([]) as FormArray;
    }
    const rule = this.rulesFormArray.at(ruleIndex);
    return rule ? (rule.get('suggestions') as FormArray) : this.formBuilder.array([]) as FormArray;
  }

  getMatchFormGroup(ruleIndex: number): FormGroup {
    if (!this.rulesForm || !this.rulesFormArray.length) {
      return this.formBuilder.group({});
    }
    const rule = this.rulesFormArray.at(ruleIndex);
    return rule ? (rule.get('match') as FormGroup) : this.formBuilder.group({});
  }

  addRule(): void {
    const index = this.rulesFormArray.length;
    this.rulesFormArray.push(this.createRuleFormGroup());
    this.collapsedRules[index] = true;
    this.collapsedSuggestions[index] = true;
  }

  removeRule(index: number): void {
    if (this.rulesFormArray.length > 1) {
      this.rulesFormArray.removeAt(index);
    }
  }

  addSuggestion(ruleIndex: number): void {
    this.getSuggestionsFormArray(ruleIndex).push(this.createSuggestionFormGroup());
  }

  removeSuggestion(ruleIndex: number, suggestionIndex: number): void {
    this.getSuggestionsFormArray(ruleIndex).removeAt(suggestionIndex);
  }

  openAddDialog(): void {
    try {
      this.isEditMode = false;
      this.selectedRecord = null;
      if (!this.rulesForm) {
        this.initForm();
      }
      this.selectedJsonFile = null;
      this.uploadedFileName = null;
      this.isFileUploaded = false;
      this.resetUploadState();
      this.dialogVisible = true;
    } catch (error) {
      console.error('Error opening dialog:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to open form. Please try again.'
      );
    }
  }

  onDialogHide(): void {
    this.dialogVisible = false;
    this.isEditMode = false;
    this.selectedRecord = null;
    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.isFileUploaded = false;
    this.resetUploadState();
    this.initForm();
  }

  onSubmit(): void {
    this.isSubmitting = true;
    this.markFormGroupTouched(this.rulesForm);

    if (!this.rulesForm.valid) {
      this.isSubmitting = false;
      const missingFields = this.getMissingRequiredFields(this.rulesForm);
      const errorMessage = missingFields.length > 0
        ? `Please fill in the following required fields: ${missingFields.join(', ')}`
        : 'Please fill in all required fields correctly.';
      this.messageHandlerService.generateToastMessage(
        'error',
        'Validation Error',
        errorMessage
      );
      return;
    }

    this.progressBarService.setProgressBarVisibility(true);

    const formValue = this.rulesForm.getRawValue();
    const payload: CreateRulePayload = {
      rules: formValue.rules.map((rule: any) => {
        const mapped: any = {
          id: rule.id,
          match: {
            type: rule.match.type,
            value: rule.match.value,
          },
        };

        if (rule.alertLabel) mapped.alertLabel = rule.alertLabel;
        if (rule.statusType) mapped.statusType = rule.statusType;
        if (rule.considerMessage) mapped.considerMessage = rule.considerMessage;
        if (rule.remindersMessage) mapped.remindersMessage = rule.remindersMessage;

        if (rule.suggestions && rule.suggestions.length > 0) {
          mapped.suggestions = rule.suggestions.map((s: any) => {
            const suggestion: any = {
              option: Number(s.option),
              suggestion: s.suggestion,
            };
            if (s.isMcqCheckedRequired) suggestion.isMcqCheckedRequired = true;
            if (s.requiredPreReqAnswer) suggestion.requiredPreReqAnswer = s.requiredPreReqAnswer;
            if (s.isAgainstOrthoGroup) suggestion.isAgainstOrthoGroup = true;
            return suggestion;
          });
        }

        return mapped;
      }),
    };

    if (this.isEditMode && this.selectedRecord) {
      const rulesToUpdate: Rule[] = Array.isArray(payload.rules) ? payload.rules : [];
      const updateRequests = rulesToUpdate.map((rule, index) => {
        const ruleId = rule?.id || this.selectedRecord?.id || this.selectedRecord?._id;
        if (!ruleId) {
          return Promise.resolve({
            index,
            ruleId: `#${index + 1}`,
            success: false,
            error: { errorMessage: 'Rule ID is missing. Cannot update rule.' },
          });
        }

        // eslint-disable-next-line no-console
        console.log('[Rules] update payload', { ruleId, rule });

        return firstValueFrom(this.rulesService.updateRule(ruleId, rule))
          .then((response) => ({ index, ruleId, success: this.isRuleSuccessResponse(response), response }))
          .catch((error) => ({ index, ruleId, success: false, error }));
      });

      Promise.all(updateRequests)
        .then((results) => {
          const failed = results.filter((r) => !r.success);
          const succeededCount = results.length - failed.length;

          failed.forEach((failure) => {
            const failureAny: any = failure;
            const errorMessage =
              failureAny?.error?.errorMessage ||
              failureAny?.error?.message ||
              failureAny?.response?.errorMessage ||
              `Failed to update rule ${failureAny.ruleId || `#${failureAny.index + 1}`}`;

            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              errorMessage
            );
          });

          if (failed.length === 0) {
            this.messageHandlerService.generateToastMessage(
              'success',
              'Success',
              `All ${results.length} rule(s) updated successfully!`
            );
            this.onDialogHide();
            this.getRules();
            return;
          }

          this.messageHandlerService.generateToastMessage(
            'warn',
            'Partial Success',
            `${succeededCount} rule(s) updated, ${failed.length} failed.`
          );
          this.getRules();
        })
        .finally(() => {
          this.progressBarService.setProgressBarVisibility(false);
          this.isSubmitting = false;
        });
    } else {
      const rulesToCreate: Rule[] = Array.isArray(payload.rules) ? payload.rules : [];
      const createRequests = rulesToCreate.map((rule, index) => {
        // eslint-disable-next-line no-console
        console.log('[Rules] create payload', rule);
        return firstValueFrom(this.rulesService.createRule(rule))
          .then((response) => ({ index, ruleId: rule.id, success: this.isRuleSuccessResponse(response), response }))
          .catch((error) => ({ index, ruleId: rule.id, success: false, error }));
      });

      Promise.all(createRequests)
        .then((results) => {
          const failed = results.filter((r) => !r.success);
          const succeededCount = results.length - failed.length;

          failed.forEach((failure) => {
            const failureAny: any = failure;
            const errorMessage =
              failureAny?.error?.errorMessage ||
              failureAny?.error?.message ||
              failureAny?.response?.errorMessage ||
              `Failed to create rule ${failureAny.ruleId || `#${failureAny.index + 1}`}`;

            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              errorMessage
            );
          });

          if (failed.length === 0) {
            this.messageHandlerService.generateToastMessage(
              'success',
              'Success',
              `All ${results.length} rule(s) created successfully!`
            );
            this.onDialogHide();
            this.getRules();
            return;
          }

          this.messageHandlerService.generateToastMessage(
            'warn',
            'Partial Success',
            `${succeededCount} rule(s) created, ${failed.length} failed.`
          );
          this.getRules();
        })
        .finally(() => {
          this.progressBarService.setProgressBarVisibility(false);
          this.isSubmitting = false;
        });
    }
  }

  private handleSubmitError(error: any): void {
    const errorMessage = error?.errorMessage || error?.message || error;

    if (
      typeof errorMessage === 'string' &&
      errorMessage.includes("You provided 'null' where a stream was expected")
    ) {
      this.isSubmitting = false;
      this.progressBarService.setProgressBarVisibility(false);
      return;
    }

    if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
      this.isSubmitting = false;
      this.progressBarService.setProgressBarVisibility(false);
      return;
    }

    this.progressBarService.setProgressBarVisibility(false);
    this.messageHandlerService.generateToastMessage(
      'error',
      ERROR_MESSAGES.COMMUNICATION,
      errorMessage
    );
    this.isSubmitting = false;
  }

  // File upload
  onJsonFileSelected(event: any): void {
    if (this.isBusy) return;

    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(LAYOUT_JSON_FILE_EXTENSION)) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Invalid File Type',
        RULES_JSON_INVALID_FILE_TYPE
      );
      return;
    }

    this.selectedJsonFile = file;
    this.uploadedFileName = file.name;
    this.uploadProgressBar = true;
    this.uploadProgressValue = 0;
    this.progressBarService.setProgressBarVisibility(true);

    const progressInterval = setInterval(() => {
      if (this.uploadProgressValue < 90) {
        this.uploadProgressValue = this.uploadProgressValue + Math.floor(Math.random() * 15) + 5;
      }
      if (this.uploadProgressValue >= 90 && this.uploadProgressValue < 100) {
        this.uploadProgressValue = 90;
      }
    }, 200);

    const fileReader = new FileReader();
    fileReader.onload = (e: any) => {
      try {
        const jsonContent = e.target?.result;
        if (!jsonContent || jsonContent.trim() === '') {
          clearInterval(progressInterval);
          this.resetUploadState();
          this.messageHandlerService.generateToastMessage(
            'error',
            'Error',
            RULES_JSON_EMPTY
          );
          return;
        }

        const jsonData = JSON.parse(jsonContent);
        clearInterval(progressInterval);
        this.uploadProgressValue = 100;

        setTimeout(() => {
          this.populateFormFromJson(jsonData);
          this.uploadProgressBar = false;
          this.uploadProgressValue = 0;
          this.progressBarService.setProgressBarVisibility(false);
        }, 500);
      } catch (error) {
        clearInterval(progressInterval);
        this.resetUploadState();
        this.messageHandlerService.generateToastMessage(
          'error',
          'Error',
          RULES_JSON_INVALID_FORMAT
        );
      }
    };

    fileReader.onerror = () => {
      clearInterval(progressInterval);
      this.resetUploadState();
      this.messageHandlerService.generateToastMessage(
        'error',
        ERROR_MESSAGES.COMMUNICATION,
        'Failed to read file'
      );
    };

    fileReader.readAsText(file);
  }

  clearSelectedFile(): void {
    if (this.isBusy) return;

    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.isFileUploaded = false;
    this.resetUploadState();
    this.initForm();
  }

  private resetUploadState(): void {
    this.uploadProgressBar = false;
    this.uploadProgressValue = 0;
    this.selectedJsonFile = null;
    this.uploadedFileName = null;
    this.progressBarService.setProgressBarVisibility(false);
  }

  private populateFormFromJson(jsonData: any): void {
    try {
      if (!this.rulesForm) {
        this.initForm();
      }
      if (!this.rulesForm) return;

      let rulesArray: any[] = [];

      if (jsonData && typeof jsonData === 'object' && !Array.isArray(jsonData)) {
        rulesArray = Array.isArray(jsonData.rules) ? jsonData.rules : [];
      } else if (Array.isArray(jsonData)) {
        rulesArray = jsonData;
      } else if (jsonData && jsonData.data && Array.isArray(jsonData.data)) {
        rulesArray = jsonData.data;
      }

      if (!Array.isArray(rulesArray) || rulesArray.length === 0) {
        this.messageHandlerService.generateToastMessage(
          'warn',
          'Warning',
          RULES_JSON_NO_RULES
        );
        return;
      }

      const limitedRules = rulesArray.slice(0, RULES_JSON_UPLOAD_LIMIT);

      const formRulesArray = this.rulesFormArray;
      while (formRulesArray.length > 0) {
        formRulesArray.removeAt(0);
      }

      limitedRules.forEach((rule, index) => {
        const ruleGroup = this.createRuleFormGroup();

        ruleGroup.patchValue({
          id: rule.id || '',
          alertLabel: rule.alertLabel || '',
          statusType: rule.statusType || '',
          considerMessage: rule.considerMessage || '',
          remindersMessage: rule.remindersMessage || '',
        });

        const matchGroup = ruleGroup.get('match') as FormGroup;
        if (rule.match && typeof rule.match === 'object') {
          matchGroup.patchValue({
            type: rule.match.type || 'EXACT',
            value: rule.match.value || '',
          });
        }

        const suggestionsArray = ruleGroup.get('suggestions') as FormArray;
        if (Array.isArray(rule.suggestions) && rule.suggestions.length > 0) {
          rule.suggestions.forEach((suggestion: any) => {
            const sugGroup = this.createSuggestionFormGroup();
            sugGroup.patchValue({
              option: suggestion.option !== undefined ? suggestion.option : null,
              suggestion: suggestion.suggestion || 'ERROR',
              isMcqCheckedRequired: suggestion.isMcqCheckedRequired || false,
              requiredPreReqAnswer: suggestion.requiredPreReqAnswer || '',
              isAgainstOrthoGroup: suggestion.isAgainstOrthoGroup || false,
            });
            suggestionsArray.push(sugGroup);
          });
        }

        formRulesArray.push(ruleGroup);

        this.collapsedRules[index] = index !== 0;
        this.collapsedSuggestions[index] = true;
      });

      this.collapsedSections = { rules: false };
      this.isFileUploaded = true;

      this.messageHandlerService.generateToastMessage(
        'success',
        'Success',
        `Successfully loaded ${limitedRules.length} rule(s) from JSON file`
      );
    } catch (error) {
      console.error('Error populating form from JSON:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to populate form from JSON file'
      );
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private getMissingRequiredFields(formGroup: FormGroup, prefix: string = ''): string[] {
    const missingFields: string[] = [];

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      const fieldPath = prefix ? `${prefix}.${key}` : key;

      if (control instanceof FormArray) {
        control.controls.forEach((arrayControl, index) => {
          const arrayPath = `${fieldPath}[${index}]`;
          if (arrayControl instanceof FormGroup) {
            missingFields.push(...this.getMissingRequiredFields(arrayControl, arrayPath));
          } else if (arrayControl.invalid && arrayControl.hasError('required')) {
            missingFields.push(fieldPath);
          }
        });
      } else if (control instanceof FormGroup) {
        missingFields.push(...this.getMissingRequiredFields(control, fieldPath));
      } else if (control && control.invalid && control.hasError('required')) {
        missingFields.push(this.getReadableFieldName(key, fieldPath));
      }
    });

    return missingFields;
  }

  private getReadableFieldName(key: string, fullPath: string): string {
    const fieldNameMap: { [key: string]: string } = {
      'id': 'Rule ID',
      'type': 'Match Type',
      'value': 'Match Value',
      'option': 'Option',
      'suggestion': 'Suggestion Type',
    };

    if (fullPath.startsWith('rules[')) {
      const ruleMatch = fullPath.match(/rules\[(\d+)\]\.(.+)/);
      if (ruleMatch) {
        const [, ruleIdx, rest] = ruleMatch;
        const ruleNum = parseInt(ruleIdx) + 1;

        if (rest.startsWith('match.')) {
          const matchField = rest.replace('match.', '');
          return `Rule ${ruleNum} - ${fieldNameMap[matchField] || matchField}`;
        }

        if (rest.startsWith('suggestions[')) {
          const sugMatch = rest.match(/suggestions\[(\d+)\]\.(\w+)/);
          if (sugMatch) {
            const [, sugIdx, sugField] = sugMatch;
            return `Rule ${ruleNum} - Suggestion ${parseInt(sugIdx) + 1} - ${fieldNameMap[sugField] || sugField}`;
          }
        }

        return `Rule ${ruleNum} - ${fieldNameMap[rest] || rest}`;
      }
    }

    if (fieldNameMap[key]) return fieldNameMap[key];
    return key;
  }

  get isBusy(): boolean {
    return this.isSubmitting;
  }

  private isRuleSuccessResponse(res: any): boolean {
    if (res?.success === true) return true;
    if (res?.statusCode >= 200 && res?.statusCode < 300) return true;
    if (res?._id && Array.isArray(res?.rules) && res.rules.length > 0) return true;
    if (res?.content && !res?.errorMessage) return true;
    return false;
  }

  toggleSection(section: string): void {
    if (this.isBusy) return;
    this.collapsedSections[section] = !this.collapsedSections[section];
  }

  toggleRule(index: number): void {
    if (this.isBusy) return;
    const isNowCollapsed = !this.collapsedRules[index];
    this.collapsedRules[index] = isNowCollapsed;

    if (isNowCollapsed) {
      this.collapsedSuggestions[index] = true;
    }
  }

  toggleSuggestions(ruleIndex: number): void {
    if (this.isBusy) return;
    this.collapsedSuggestions[ruleIndex] = !this.collapsedSuggestions[ruleIndex];
  }

  isRuleCollapsed(index: number): boolean {
    return this.collapsedRules[index] === true;
  }

  isSuggestionsCollapsed(ruleIndex: number): boolean {
    return this.collapsedSuggestions[ruleIndex] === true;
  }

  onDelete(record: any): void {
    if (record?.id === undefined && !record?._id) return;

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this rule?',
      header: 'Delete Rule',
      icon: 'pi pi-trash',
      acceptButtonStyleClass: 'btn-delete',
      accept: () => {
        this.progressBarService.setProgressBarVisibility(true);
        const recordId = record.id ?? record._id;

        this.subscriptions.push(
          this.rulesService.deleteRule(recordId).subscribe({
            next: (res) => {
              if (res === null || res === undefined || (typeof res === 'object' && Object.keys(res || {}).length === 0)) {
                this.messageHandlerService.generateToastMessage(
                  'success',
                  'Success',
                  'Rule deleted successfully'
                );
                this.getRules();
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              if (this.isSuccessResponse(res)) {
                this.messageHandlerService.generateToastMessage(
                  'success',
                  'Success',
                  'Rule deleted successfully'
                );
                this.getRules();
              } else {
                this.messageHandlerService.generateToastMessage(
                  'error',
                  ERROR_MESSAGES.COMMUNICATION,
                  res?.errorMessage || res?.message || 'Failed to delete rule'
                );
              }
              this.progressBarService.setProgressBarVisibility(false);
            },
            error: (error) => {
              const errorMessage = error?.errorMessage || error?.message || error;

              if (
                typeof errorMessage === 'string' &&
                errorMessage.includes("You provided 'null' where a stream was expected")
              ) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              if (error?.status === 403 || error?.status === 400 || error?.status === 500) {
                this.progressBarService.setProgressBarVisibility(false);
                return;
              }

              this.progressBarService.setProgressBarVisibility(false);
              this.messageHandlerService.generateToastMessage(
                'error',
                ERROR_MESSAGES.COMMUNICATION,
                errorMessage
              );
            },
          })
        );
      },
    });
  }

  onRowClick(record: any): void {
    this.openEditDialog(record);
  }

  onEditRule(event: Event, record: any): void {
    event.stopPropagation();
    this.openEditDialog(record);
  }

  private downloadJson(data: unknown, filename: string): void {
    const json = JSON.stringify(data ?? {}, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  exportRules(): void {
    if (this.isExportingRules || this.isBusy) {
      return;
    }

    this.isExportingRules = true;
    this.progressBarService.setProgressBarVisibility(true);

    this.rulesService
      .getAll({
        page: 1,
        limit: 99999, // to get all possible maximum rules from the database
        sortBy: 'createdAt',
        sortOrder: 'desc',
      })
      .pipe(
        finalize(() => {
          this.isExportingRules = false;
          this.progressBarService.setProgressBarVisibility(false);
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!this.isSuccessResponse(res)) {
            this.messageHandlerService.generateToastMessage(
              'error',
              ERROR_MESSAGES.COMMUNICATION,
              res?.errorMessage || 'Failed to export rules'
            );
            return;
          }

          const data = Array.isArray(res?.data) ? res.data : [];
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          this.downloadJson(data, `rules-export-${timestamp}.json`);

          this.messageHandlerService.generateToastMessage(
            'success',
            'Success',
            `Exported ${data.length} rule(s) successfully`
          );
        },
        error: (error) => {
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.COMMUNICATION,
            error?.errorMessage || error?.message || 'Failed to export rules'
          );
        },
      });
  }

  openEditDialog(record: any): void {
    try {
      this.isEditMode = true;
      this.selectedRecord = record;

      if (!this.rulesForm) {
        this.initForm();
      }
      if (!this.rulesForm) return;

      this.selectedJsonFile = null;
      this.uploadedFileName = null;
      this.isFileUploaded = true;
      this.resetUploadState();

      this.populateFormFromRecord(record);

      this.dialogVisible = true;
    } catch (error) {
      console.error('Error opening edit dialog:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to open form for editing. Please try again.'
      );
    }
  }

  private populateFormFromRecord(recordData: any): void {
    if (!this.rulesForm || !recordData) return;

    try {
      const formRulesArray = this.rulesFormArray;
      while (formRulesArray.length > 0) {
        formRulesArray.removeAt(0);
      }

      const rulesData = Array.isArray(recordData.rules) && recordData.rules.length > 0
        ? recordData.rules
        : [{
            id: recordData.id || '',
            alertLabel: recordData.alertLabel || '',
            statusType: recordData.statusType || '',
            considerMessage: recordData.considerMessage || '',
            remindersMessage: recordData.remindersMessage || '',
            match: recordData.match || { type: 'EXACT', value: '' },
            suggestions: Array.isArray(recordData.suggestions) ? recordData.suggestions : [],
          }];

      if (rulesData.length > 0) {
        rulesData.forEach((rule: any, index: number) => {
          const ruleGroup = this.createRuleFormGroup();

          ruleGroup.patchValue({
            id: rule.id || '',
            alertLabel: rule.alertLabel || '',
            statusType: rule.statusType || '',
            considerMessage: rule.considerMessage || '',
            remindersMessage: rule.remindersMessage || '',
          });

          const matchGroup = ruleGroup.get('match') as FormGroup;
          if (rule.match && typeof rule.match === 'object') {
            matchGroup.patchValue({
              type: rule.match.type || 'EXACT',
              value: rule.match.value || '',
            });
          }

          const suggestionsArray = ruleGroup.get('suggestions') as FormArray;
          if (Array.isArray(rule.suggestions) && rule.suggestions.length > 0) {
            rule.suggestions.forEach((suggestion: any) => {
              const sugGroup = this.createSuggestionFormGroup();
              sugGroup.patchValue({
                option: suggestion.option !== undefined ? suggestion.option : null,
                suggestion: suggestion.suggestion || 'ERROR',
                isMcqCheckedRequired: suggestion.isMcqCheckedRequired || false,
                requiredPreReqAnswer: suggestion.requiredPreReqAnswer || '',
                isAgainstOrthoGroup: suggestion.isAgainstOrthoGroup || false,
              });
              suggestionsArray.push(sugGroup);
            });
          }

          formRulesArray.push(ruleGroup);

          this.collapsedRules[index] = index !== 0;
          this.collapsedSuggestions[index] = true;
        });
      } else {
        formRulesArray.push(this.createRuleFormGroup());
        this.collapsedRules[0] = false;
      }

      this.collapsedSections = { rules: false };
    } catch (error) {
      console.error('Error populating form from record data:', error);
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Failed to populate form with rule data'
      );
    }
  }

  getFirstRuleId(record: any): string {
    if (record?.id != null && record.id !== '') {
      return String(record.id);
    }
    if (record?.rules && record.rules.length > 0) {
      return record.rules[0].id || '-';
    }
    const flatId = record?._id ?? record?.ruleType ?? record?.title;
    if (flatId != null && flatId !== '') {
      return String(flatId);
    }
    return '-';
  }

  getFirstMatchType(record: any): string {
    if (record?.match?.type != null && record.match.type !== '') {
      return String(record.match.type);
    }
    if (record?.rules && record.rules.length > 0 && record.rules[0].match) {
      return record.rules[0].match.type || '-';
    }
    if (record?.GGOperator != null && record.GGOperator !== '') {
      return String(record.GGOperator);
    }
    return '-';
  }

  getFirstAlertLabel(record: any): string {
    if (record?.alertLabel != null && record.alertLabel !== '') {
      return String(record.alertLabel);
    }
    if (record?.rules && record.rules.length > 0) {
      return record.rules[0].alertLabel || '-';
    }
    if (record?.GGType != null && record.GGType !== '') {
      return String(record.GGType);
    }
    return '-';
  }

  getFirstStatusType(record: any): string {
    if (record?.statusType != null && record.statusType !== '') {
      return String(record.statusType);
    }
    if (record?.rules && record.rules.length > 0) {
      return record.rules[0].statusType || '-';
    }
    if (record?.group != null && record.group !== '') {
      return String(record.group);
    }
    if (record?.visitType != null && record.visitType !== '') {
      return String(record.visitType);
    }
    return '-';
  }

  getRulesCount(record: any): number {
    if (
      record?.id != null ||
      record?.match != null ||
      record?.alertLabel != null ||
      record?.statusType != null
    ) {
      return 1;
    }
    if (Array.isArray(record?.rules) && record.rules.length > 0) {
      return record.rules.length;
    }
    if (record && (record.ruleType || record.title || record._id || record.id)) {
      return 1;
    }
    return 0;
  }

  getTotalSuggestions(record: any): number {
    if (Array.isArray(record?.suggestions)) {
      return record.suggestions.length;
    }
    if (!Array.isArray(record?.rules)) {
      return 0;
    }
    return record.rules.reduce((acc: number, rule: any) => acc + (rule.suggestions?.length || 0), 0);
  }
}
